import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, 
  Clock, 
  MapPin, 
  Wrench, 
  CloudRain, 
  Zap,
  User,
  Truck,
  ChevronRight,
  Eye,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { alertsAPI } from '../../services/api'; // Import from api.js

const RecentAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  // Color constants
  const PRIMARY_COLOR = '#e28743';
  const SECONDARY_COLOR = '#304a3f';

  useEffect(() => {
    fetchRecentAlerts();
  }, []);

  const fetchRecentAlerts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await alertsAPI.getAll();
      
      // Handle response structure
      let recentAlerts = [];
      if (response && response.success && Array.isArray(response.data)) {
        recentAlerts = response.data.slice(0, 6);
      } else if (Array.isArray(response)) {
        recentAlerts = response.slice(0, 6);
      } else if (response && Array.isArray(response.data)) {
        recentAlerts = response.data.slice(0, 6);
      }
      
      setAlerts(recentAlerts);
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError('Failed to load alerts');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-gradient-to-r from-red-500 to-red-600';
      case 'high': return 'bg-gradient-to-r from-orange-500 to-orange-600';
      case 'medium': return `bg-gradient-to-r from-[${PRIMARY_COLOR}] to-orange-500`;
      case 'low': return `bg-gradient-to-r from-[${SECONDARY_COLOR}] to-blue-600`;
      default: return 'bg-gradient-to-r from-gray-500 to-gray-600';
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'critical':
        return (
          <span className="px-2 sm:px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            <span className="hidden sm:inline">CRITICAL</span>
            <span className="sm:hidden">CRIT</span>
          </span>
        );
      case 'high':
        return (
          <span className="px-2 sm:px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-200 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            <span className="hidden sm:inline">HIGH</span>
            <span className="sm:hidden">HIGH</span>
          </span>
        );
      case 'medium':
        return (
          <span className="px-2 sm:px-3 py-1 rounded-full text-xs font-semibold" style={{ 
            backgroundColor: `${PRIMARY_COLOR}15`,
            color: PRIMARY_COLOR,
            borderColor: `${PRIMARY_COLOR}30`
          }}>
            <AlertTriangle className="w-3 h-3" />
            <span className="hidden sm:inline">MEDIUM</span>
            <span className="sm:hidden">MED</span>
          </span>
        );
      default:
        return (
          <span className="px-2 sm:px-3 py-1 rounded-full text-xs font-semibold" style={{ 
            backgroundColor: `${SECONDARY_COLOR}15`,
            color: SECONDARY_COLOR,
            borderColor: `${SECONDARY_COLOR}30`
          }}>
            <AlertCircle className="w-3 h-3" />
            <span className="hidden sm:inline">LOW</span>
            <span className="sm:hidden">LOW</span>
          </span>
        );
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'fatigue':
        return <Clock className="w-4 sm:w-5 h-4 sm:h-5 text-purple-600" />;
      case 'geofence':
        return <MapPin className="w-4 sm:w-5 h-4 sm:h-5 text-blue-600" />;
      case 'maintenance':
        return <Wrench className="w-4 sm:w-5 h-4 sm:h-5" style={{ color: PRIMARY_COLOR }} />;
      case 'weather':
        return <CloudRain className="w-4 sm:w-5 h-4 sm:h-5 text-blue-400" />;
      case 'speed':
        return <Zap className="w-4 sm:w-5 h-4 sm:h-5 text-yellow-600" />;
      default:
        return <AlertCircle className="w-4 sm:w-5 h-4 sm:h-5 text-gray-600" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'fatigue': return 'FATIGUE';
      case 'geofence': return 'GEOFENCE';
      case 'maintenance': return 'MAINTENANCE';
      case 'weather': return 'WEATHER';
      case 'speed': return 'SPEED';
      case 'system': return 'SYSTEM';
      default: return type?.toUpperCase() || 'ALERT';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
            backgroundColor: `${PRIMARY_COLOR}15`,
            color: PRIMARY_COLOR
          }}>
            ACTIVE
          </span>
        );
      case 'resolved':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            <span className="hidden sm:inline">RESOLVED</span>
            <span className="sm:hidden">RES</span>
          </span>
        );
      case 'acknowledged':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <span className="hidden sm:inline">ACKNOWLEDGED</span>
            <span className="sm:hidden">ACK</span>
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status?.toUpperCase() || 'UNKNOWN'}
          </span>
        );
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'Just now';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Unknown time';
    }
  };

  const handleViewAll = () => {
    navigate('/alerts');
  };

  const handleViewAlert = (alertId) => {
    navigate(`/alerts?view=${alertId}`);
  };

  const handleResolve = async (alertId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to resolve this alert?')) {
      return;
    }

    try {
      const response = await alertsAPI.markAsResolved(alertId);
      
      if (response && response.success) {
        setAlerts(prevAlerts => 
          prevAlerts.map(alert => 
            alert._id === alertId 
              ? { ...alert, status: 'resolved', resolvedAt: new Date().toISOString() } 
              : alert
          )
        );
      } else {
        alert(response?.message || 'Failed to resolve alert');
      }
    } catch (err) {
      console.error('Error resolving alert:', err);
      alert('Failed to resolve alert. Please try again.');
    }
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'company_admin' || user.role === 'super_admin';

  return (
    <div className="bg-white rounded-lg sm:rounded-xl shadow-sm sm:shadow border border-gray-100 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2 sm:p-3 rounded-lg sm:rounded-xl" style={{ 
            backgroundColor: `${PRIMARY_COLOR}10`
          }}>
            <AlertTriangle className="w-5 sm:w-6 h-5 sm:h-6" style={{ color: PRIMARY_COLOR }} />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Recent Alerts</h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">Latest safety and operational alerts</p>
          </div>
        </div>
        <button 
          onClick={handleViewAll}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 text-white rounded-lg sm:rounded-xl hover:shadow-md transition-all duration-300 group text-sm sm:text-base"
          style={{ 
            background: `linear-gradient(135deg, ${PRIMARY_COLOR}, ${SECONDARY_COLOR})`
          }}
        >
          <span className="font-semibold whitespace-nowrap">View All</span>
          <ChevronRight className="w-3 sm:w-4 h-3 sm:h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-8 sm:py-12">
          <div className="animate-spin rounded-full h-10 sm:h-12 w-10 sm:w-12 border-b-2" style={{ borderColor: PRIMARY_COLOR }}></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6 text-center">
          <AlertCircle className="w-8 sm:w-12 h-8 sm:h-12 text-red-500 mx-auto mb-2 sm:mb-3" />
          <p className="text-red-600 text-sm sm:text-base">{error}</p>
          <button
            onClick={fetchRecentAlerts}
            className="mt-3 sm:mt-4 px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm sm:text-base"
          >
            Retry
          </button>
        </div>
      ) : alerts.length === 0 ? (
        <div className="text-center py-8 sm:py-12">
          <div className="w-16 sm:w-20 h-16 sm:h-20 mx-auto mb-3 sm:mb-4 rounded-full flex items-center justify-center" style={{ 
            backgroundColor: `${SECONDARY_COLOR}10`
          }}>
            <AlertTriangle className="w-8 sm:w-10 h-8 sm:h-10" style={{ color: SECONDARY_COLOR }} />
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-2">No Recent Alerts</h3>
          <p className="text-gray-500 text-sm sm:text-base">All systems are operating normally</p>
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {alerts.map((alert) => (
            <div 
              key={alert._id}
              onClick={() => handleViewAlert(alert._id)}
              className="group relative bg-white border border-gray-200 rounded-lg sm:rounded-xl p-3 sm:p-5 hover:border-blue-300 hover:shadow-sm sm:hover:shadow transition-all duration-300 cursor-pointer overflow-hidden"
            >
              {/* Severity indicator line */}
              <div className={`absolute left-0 top-0 bottom-0 w-1 ${getSeverityColor(alert.severity)}`} />
              
              {/* Alert content */}
              <div className="ml-3 sm:ml-4">
                <div className="flex items-start justify-between mb-2 sm:mb-3">
                  <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                    <div className="p-1.5 sm:p-2 rounded-lg flex-shrink-0" style={{ 
                      backgroundColor: `${SECONDARY_COLOR}05`,
                      border: `1px solid ${SECONDARY_COLOR}20`
                    }}>
                      {getTypeIcon(alert.type)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                        <h3 className="text-sm sm:text-base lg:text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                          {alert.title || getTypeLabel(alert.type)}
                        </h3>
                      </div>
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                        {getSeverityBadge(alert.severity)}
                        {getStatusBadge(alert.status)}
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mt-1">
                        {alert.message || 'No description available'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-1 sm:gap-2 flex-shrink-0 ml-2">
                    <span className="text-xs font-medium text-gray-500 whitespace-nowrap">
                      {formatTime(alert.createdAt)}
                    </span>
                    <Eye className="w-3 sm:w-4 h-3 sm:h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>

                {/* Alert metadata */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
                  {(alert.driver || alert.vehicle) && (
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                      {alert.driver && (
                        <div className="flex items-center gap-2">
                          <div className="p-1 sm:p-1.5 rounded-lg" style={{ 
                            backgroundColor: `${PRIMARY_COLOR}10`,
                            border: `1px solid ${PRIMARY_COLOR}30`
                          }}>
                            <User className="w-3 sm:w-4 h-3 sm:h-4" style={{ color: PRIMARY_COLOR }} />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Driver</p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate max-w-[120px] sm:max-w-none">
                              {alert.driver.driverId || 'Unknown'}
                              {alert.driver.name && ` (${alert.driver.name})`}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {alert.vehicle && (
                        <div className="flex items-center gap-2">
                          <div className="p-1 sm:p-1.5 rounded-lg" style={{ 
                            backgroundColor: `${SECONDARY_COLOR}10`,
                            border: `1px solid ${SECONDARY_COLOR}30`
                          }}>
                            <Truck className="w-3 sm:w-4 h-3 sm:h-4" style={{ color: SECONDARY_COLOR }} />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Vehicle</p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate max-w-[120px] sm:max-w-none">
                              {alert.vehicle.registration || 'Unknown'}
                              {alert.vehicle.model && ` (${alert.vehicle.model})`}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 sm:ml-auto">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewAlert(alert._id);
                      }}
                      className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-colors flex items-center gap-1 flex-shrink-0"
                      style={{ 
                        color: PRIMARY_COLOR,
                        backgroundColor: `${PRIMARY_COLOR}10`
                      }}
                    >
                      <Eye className="w-3 h-3" />
                      <span className="hidden sm:inline">Details</span>
                      <span className="sm:hidden">View</span>
                    </button>
                    
                    {isAdmin && alert.status === 'active' && (
                      <button
                        onClick={(e) => handleResolve(alert._id, e)}
                        className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium text-white rounded-lg transition-all shadow-sm hover:shadow flex-shrink-0"
                        style={{ 
                          background: `linear-gradient(135deg, ${SECONDARY_COLOR}, ${PRIMARY_COLOR})`
                        }}
                      >
                        <span className="hidden sm:inline">Resolve</span>
                        <span className="sm:hidden">Fix</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer with stats */}
      {alerts.length > 0 && (
        <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div className="text-center p-2 sm:p-3 rounded-lg" style={{ 
              backgroundColor: `${SECONDARY_COLOR}05`
            }}>
              <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{alerts.length}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
            <div className="text-center p-2 sm:p-3 rounded-lg" style={{ 
              backgroundColor: `${PRIMARY_COLOR}05`
            }}>
              <div className="text-lg sm:text-xl lg:text-2xl font-bold" style={{ color: PRIMARY_COLOR }}>
                {alerts.filter(a => a.status === 'active').length}
              </div>
              <div className="text-xs text-gray-500">Active</div>
            </div>
            <div className="text-center p-2 sm:p-3 rounded-lg bg-red-50">
              <div className="text-lg sm:text-xl lg:text-2xl font-bold text-red-600">
                {alerts.filter(a => a.severity === 'critical' || a.severity === 'high').length}
              </div>
              <div className="text-xs text-gray-500">High Priority</div>
            </div>
            <div className="text-center p-2 sm:p-3 rounded-lg bg-green-50">
              <div className="text-lg sm:text-xl lg:text-2xl font-bold text-green-600">
                {alerts.filter(a => a.status === 'resolved').length}
              </div>
              <div className="text-xs text-gray-500">Resolved</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentAlerts;